import {getDepartmentHealthBenchmarkReference} from './getDepartmentHealthBenchmarkReference';
import {BENCHMARK_DELTA_THRESHOLD} from '../../../../../components/ScoreStatCard/scoreStatUtils';
import {orangeColors, scorePill} from '../../../../../layouts/theme/designSystem';
import type {
  DepartmentHealthBenchmarks,
  DepartmentHealthDetail,
  DepartmentHealthNarrativeDimension,
  DepartmentHealthState,
} from '../../types/DepartmentHealthTypes';
import {DEPARTMENT_HEALTH_NARRATIVE_DIMENSIONS} from '../../types/DepartmentHealthTypes';

export type NarrativeSignalColors = {
  signalColor: string;
};

export const getNarrativeSignalColors = (healthState: DepartmentHealthState): NarrativeSignalColors => {
  switch (healthState) {
    case 'well_aligned':
      return {signalColor: scorePill.high.strokeColor};
    case 'functional':
      return {signalColor: orangeColors[100]};
    case 'at_risk':
      return {signalColor: scorePill.low.strokeColor};
    default:
      return {signalColor: scorePill.high.strokeColor};
  }
};

export type AboveBelowBenchmark = 'above' | 'below' | 'inLineWith';

export const getAboveBelowBenchmark = (benchmarkDelta: number | null): AboveBelowBenchmark => {
  if (benchmarkDelta === null) {
    return 'inLineWith';
  }

  if (benchmarkDelta >= BENCHMARK_DELTA_THRESHOLD) {
    return 'above';
  }
  if (benchmarkDelta <= -BENCHMARK_DELTA_THRESHOLD) {
    return 'below';
  }

  return 'inLineWith';
};

export const getWeakestNarrativeDimension = (
  benchmarks: DepartmentHealthBenchmarks
): DepartmentHealthNarrativeDimension | null => {
  const deltas = benchmarks.deltas;
  if (!deltas) {
    return null;
  }

  let weakest: DepartmentHealthNarrativeDimension | null = null;
  let weakestDelta = Number.POSITIVE_INFINITY;

  DEPARTMENT_HEALTH_NARRATIVE_DIMENSIONS.forEach((dimension) => {
    const delta = deltas[dimension];
    if (delta === null) {
      return;
    }

    if (delta < weakestDelta) {
      weakestDelta = delta;
      weakest = dimension;
    }
  });

  return weakest;
};

export const isWeakestDimensionBelowBenchmark = (
  benchmarks: DepartmentHealthBenchmarks,
  dimension: DepartmentHealthNarrativeDimension
): boolean => {
  const delta = benchmarks.deltas?.[dimension];
  if (delta === null || delta === undefined) {
    return false;
  }

  return delta < 0;
};

export interface DepartmentHealthNarrativeContext {
  deptAverage: number | null;
  benchmarkOverall: number | null;
  benchmarkDelta: number | null;
  aboveBelow: AboveBelowBenchmark;
  profileCount: number;
  strongFitCount: number;
  lowProfileCount: number;
  weakestDimension: DepartmentHealthNarrativeDimension | null;
  weakestBelowBenchmark: boolean;
  redeploymentCount: number;
}

export const buildDepartmentHealthNarrativeContext = (
  health: DepartmentHealthDetail,
  benchmarks: DepartmentHealthBenchmarks
): DepartmentHealthNarrativeContext => {
  const deptAverage = health.scores.departmentAverage?.overall ?? null;
  const benchmarkOverall = getDepartmentHealthBenchmarkReference(benchmarks, 'overall');
  const benchmarkDelta = benchmarks.deltas?.overall ?? null;
  const weakestDimension = getWeakestNarrativeDimension(benchmarks);

  return {
    deptAverage,
    benchmarkOverall,
    benchmarkDelta,
    aboveBelow: getAboveBelowBenchmark(benchmarkDelta),
    profileCount: health.scores.profileCount,
    strongFitCount: health.scores.profilesAtOrAboveLimit,
    lowProfileCount: health.scores.profilesBelowLimit,
    weakestDimension,
    weakestBelowBenchmark: weakestDimension
      ? isWeakestDimensionBelowBenchmark(benchmarks, weakestDimension)
      : false,
    redeploymentCount: health.profileDecisionGuide.redeployCandidates.length,
  };
};
